import { useCallback, useEffect, useState } from "react"
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, type CarouselApi } from "../components/ui/carousel"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer, IteratedElement } from "./element-renderer"
import { cn } from "../lib/utils"
import { positiveInteger } from "./property-value-utils"

function resolveCurrentSlide(
  rawValue: unknown,
  slideCount: number,
  resolveSymbol: (value: string) => unknown,
): number {
  const resolved = typeof rawValue === "string"
    ? resolveSymbol(rawValue)
    : rawValue
  const numeric = Number(resolved)
  if (!Number.isInteger(numeric) || numeric < 0 || slideCount === 0) return 0
  return Math.min(numeric, slideCount - 1)
}

export function CarouselRenderer({ element }: RendererProps) {
  const ctx = useRenderContext()
  const { dispatchEvent, resolveSymbol } = ctx
  const [api, setApi] = useState<CarouselApi | undefined>(undefined)
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  // Note: `element.variant === "auto"` is a legacy autoplay-behavior flag
  // used by the autoplay useEffect below; data-variant carries the raw
  // token, which overlaps with that behavior path harmlessly.
  const atomDefaults = useAtomDefaults("carousel")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  // Support iterateOver: expand single child template into N slides
  let slides = element.children ?? []
  let iterItems: unknown[] | null = null
  const itemVar = element.itemVar
  if (element.iterateOver && slides.length === 1) {
    const items = resolveSymbol(element.iterateOver)
    if (Array.isArray(items) && items.length > 0) {
      iterItems = items
      const template = slides[0]
      slides = items.map((_item, i) => ({
        ...template,
        id: `${template.id}__slide_${i}`,
      }))
    }
  }

  const slideCount = slides.length
  const rawCurrentSlide = (element as unknown as { currentSlide?: unknown }).currentSlide
  const authoredCurrentSlide = resolveCurrentSlide(
    rawCurrentSlide,
    slideCount,
    resolveSymbol,
  )
  const [currentSlide, setCurrentSlide] = useState(authoredCurrentSlide)
  const rawInterval = (element as unknown as { interval?: unknown }).interval
  const intervalMs = positiveInteger(
    typeof rawInterval === "string" ? resolveSymbol(rawInterval) : rawInterval,
    4000,
    600_000,
  )
  const shouldAutoplay = typeof element.autoplay === "boolean"
    ? element.autoplay
    : element.variant === "auto"
  const propertyDataAttrs = {
    "data-autoplay": shouldAutoplay ? "true" : "false",
    "data-interval": String(intervalMs),
    "data-current-slide": String(currentSlide),
  }

  // The Embla API is established asynchronously by the carousel primitive.
  const handleSetApi = useCallback(
    (nextApi: CarouselApi) => setApi(nextApi),
    [],
  )

  // Authored state controls the visible slide. Keeping the local value gives
  // immediate interaction feedback while a host updates the authored state.
  useEffect(() => {
    setCurrentSlide(authoredCurrentSlide)
    if (api && api.selectedScrollSnap() !== authoredCurrentSlide) {
      api.scrollTo(authoredCurrentSlide)
    }
  }, [api, authoredCurrentSlide])

  // Surface both interaction changes and the API's initial/external selection
  // through the same runtime event contract.
  useEffect(() => {
    if (!api) return
    const handleSelect = () => {
      const index = api.selectedScrollSnap()
      setCurrentSlide(index)
      dispatchEvent(element.id, "slideChange", {
        index,
        currentSlide: index,
      })
    }
    handleSelect()
    api.on("select", handleSelect)
    api.on("reInit", handleSelect)
    return () => {
      api.off("select", handleSelect)
      api.off("reInit", handleSelect)
    }
  }, [api, element.id, dispatchEvent])

  // Auto-advance when variant is "auto"
  useEffect(() => {
    if (shouldAutoplay && slideCount > 1 && api) {
      const interval = setInterval(() => {
        const next = ((api.selectedScrollSnap?.() ?? 0) + 1) % slideCount
        api.scrollTo(next)
      }, intervalMs)
      return () => clearInterval(interval)
    }
  }, [api, shouldAutoplay, intervalMs, slideCount])

  if (slideCount === 0) {
    return (
      <div
        className={cn(
          "flex h-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground",
          element.className,
        )}
        data-testid={`carousel-${element.id}`}
        data-atom-type="carousel"
        data-variant={resolvedVariant}
        data-size={resolvedSize}
        {...propertyDataAttrs}
      >
        No slides
      </div>
    )
  }

  return (
    <Carousel
      className={cn("w-full", element.className)}
      data-testid={`carousel-${element.id}`}
      data-atom-type="carousel"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      {...propertyDataAttrs}
      setApi={handleSetApi}
      opts={{ loop: true }}
    >
      <CarouselContent>
        {slides.map((slide, i) => (
          <CarouselItem key={slide.id}>
            {iterItems && itemVar ? (
              <IteratedElement
                element={slide}
                item={iterItems[i]}
                itemVar={itemVar}
                parentCtx={ctx}
                childIndex={i}
              />
            ) : (
              <ElementRenderer element={slide} />
            )}
          </CarouselItem>
        ))}
      </CarouselContent>
      {slideCount > 1 && (
        <>
          <CarouselPrevious />
          <CarouselNext />
        </>
      )}
    </Carousel>
  )
}
